import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API } from "@/App";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Trash2, GripVertical, Save, RefreshCw,
  Eye, Edit2, ChevronUp, ChevronDown, Copy, FileText, Mail,
  FileSignature, CheckSquare, Circle, List, Type, Hash, Calendar,
  Upload, MessageSquare, Settings, Sparkles, X
} from "lucide-react";

// Question type configurations
const QUESTION_TYPES = [
  { id: "text", label: "Text Input", icon: Type, description: "Single line text" },
  { id: "email", label: "Email", icon: Mail, description: "Email address" },
  { id: "phone", label: "Phone", icon: Hash, description: "Phone number" },
  { id: "textarea", label: "Long Text", icon: MessageSquare, description: "Multi-line text" },
  { id: "select", label: "Single Select", icon: Circle, description: "Choose one option" },
  { id: "multiselect", label: "Multi Select", icon: CheckSquare, description: "Choose multiple options" },
  { id: "date", label: "Date", icon: Calendar, description: "Date picker" },
  { id: "file", label: "File Upload", icon: Upload, description: "Upload images/files" },
];

// Question Editor Component
function QuestionEditor({ question, onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const [optionInput, setOptionInput] = useState("");

  const handleAddOption = () => {
    if (!optionInput.trim()) return;
    const options = [...(question.options || []), optionInput.trim()];
    onUpdate({ ...question, options });
    setOptionInput("");
  };

  const handleRemoveOption = (idx) => {
    const options = question.options.filter((_, i) => i !== idx);
    onUpdate({ ...question, options });
  };

  const TypeIcon = QUESTION_TYPES.find(t => t.id === question.type)?.icon || Type;
  const showOptions = ["select", "multiselect"].includes(question.type);

  return (
    <div className="border rounded-lg bg-white shadow-sm" data-testid={`question-${question.id}`}>
      {/* Header - Always visible */}
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${question.required ? "bg-purple-100 text-purple-600" : "bg-gray-100 text-gray-500"}`}>
          <TypeIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{question.label || "Untitled Question"}</p>
          <p className="text-xs text-gray-500">{QUESTION_TYPES.find(t => t.id === question.type)?.label || question.type}</p>
        </div>
        <div className="flex items-center gap-1">
          {question.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst}>
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast}>
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-500" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Expanded Editor */}
      {expanded && (
        <div className="border-t p-4 space-y-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Question Label</Label>
              <Input
                value={question.label || ""}
                onChange={(e) => onUpdate({ ...question, label: e.target.value })}
                placeholder="Enter question text"
                data-testid={`question-label-${question.id}`}
              />
            </div>
            <div>
              <Label className="text-xs">Question Type</Label>
              <Select value={question.type} onValueChange={(v) => onUpdate({ ...question, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Placeholder Text</Label>
              <Input
                value={question.placeholder || ""}
                onChange={(e) => onUpdate({ ...question, placeholder: e.target.value })}
                placeholder="Hint text shown in field"
              />
            </div>
            <div className="flex items-center gap-4 pt-5">
              <div className="flex items-center gap-2">
                <Switch
                  checked={question.required || false}
                  onCheckedChange={(v) => onUpdate({ ...question, required: v })}
                />
                <Label className="text-xs">Required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={question.has_other || false}
                  onCheckedChange={(v) => onUpdate({ ...question, has_other: v })}
                />
                <Label className="text-xs">Allow "Other"</Label>
              </div>
            </div>
          </div>

          {/* Options for select/multiselect */}
          {showOptions && (
            <div>
              <Label className="text-xs mb-2 block">Options</Label>
              <div className="space-y-2">
                {(question.options || []).map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-1 bg-white border rounded px-3 py-1.5 text-sm">
                      {question.type === "multiselect" ? (
                        <CheckSquare className="w-3 h-3 inline mr-2 text-gray-400" />
                      ) : (
                        <Circle className="w-3 h-3 inline mr-2 text-gray-400" />
                      )}
                      {opt}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveOption(idx)} className="text-red-500">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddOption()}
                    placeholder="Add option..."
                    className="text-sm"
                  />
                  <Button size="sm" onClick={handleAddOption}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Add Question Dialog
function AddQuestionDialog({ isOpen, onClose, onAdd }) {
  const [selectedType, setSelectedType] = useState("text");

  const handleAdd = () => {
    onAdd({
      id: `q_${Date.now()}`,
      type: selectedType,
      label: "",
      placeholder: "",
      required: false,
      options: ["select", "multiselect"].includes(selectedType) ? [] : undefined,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Question</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-4">
          {QUESTION_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`p-4 rounded-lg border text-left transition-colors ${
                selectedType === type.id
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
              data-testid={`add-q-type-${type.id}`}
            >
              <type.icon className={`w-5 h-5 mb-2 ${selectedType === type.id ? "text-purple-600" : "text-gray-500"}`} />
              <p className="font-medium text-sm">{type.label}</p>
              <p className="text-xs text-gray-500">{type.description}</p>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAdd} data-testid="confirm-add-question">Add Question</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Preview Dialog
function PreviewDialog({ isOpen, onClose, questions, categoryColors }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" /> Intake Form Preview
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1">
          <div className="space-y-6 p-4">
            {questions.map((q, idx) => (
              <div key={q.id} className="space-y-2">
                <Label className="text-sm font-medium">
                  {q.label || "Untitled Question"}
                  {q.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                
                {q.type === "text" && (
                  <Input placeholder={q.placeholder || "Enter text..."} disabled />
                )}
                {q.type === "email" && (
                  <Input type="email" placeholder={q.placeholder || "email@example.com"} disabled />
                )}
                {q.type === "phone" && (
                  <Input placeholder={q.placeholder || "+1 (555) 000-0000"} disabled />
                )}
                {q.type === "textarea" && (
                  <Textarea placeholder={q.placeholder || "Enter your response..."} disabled rows={3} />
                )}
                {q.type === "date" && (
                  <Input type="date" disabled />
                )}
                {q.type === "file" && (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center text-gray-400">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click or drag to upload</p>
                  </div>
                )}
                {q.type === "select" && (
                  <Select disabled>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {(q.options || []).map((opt, i) => (
                        <SelectItem key={i} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {q.type === "multiselect" && (
                  <div className="flex flex-wrap gap-2">
                    {(q.options || []).map((opt, i) => (
                      <button
                        key={i}
                        className="px-3 py-1.5 rounded-full border border-gray-200 text-sm hover:border-purple-300 hover:bg-purple-50 transition-colors"
                      >
                        {opt}
                      </button>
                    ))}
                    {q.has_other && (
                      <button className="px-3 py-1.5 rounded-full border border-dashed border-gray-300 text-sm text-gray-500">
                        + Other
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button onClick={onClose}>Close Preview</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function TemplateEditorTab({ subCategoryData, categoryColors, category, subCategoryId, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Derive the correct category to use for API calls
  // Priority: subCategoryData.preset_id > category prop
  const effectiveCategory = subCategoryData?.preset_id || category;

  useEffect(() => {
    fetchTemplate();
  }, [effectiveCategory, subCategoryData?.sections]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      // FIRST: Check if the portal has its own sections saved
      if (subCategoryData?.sections && subCategoryData.sections.length > 0) {
        // Build flat questions array from sections
        const flatQuestions = [];
        for (const section of subCategoryData.sections) {
          for (const q of (section.questions || [])) {
            flatQuestions.push({
              ...q, 
              section_id: section.id, 
              section_title: section.title
            });
          }
        }
        setTemplate({
          sections: subCategoryData.sections,
          intake_questions: flatQuestions,
          branding: subCategoryData.branding || {}
        });
        setQuestions(flatQuestions);
        setLoading(false);
        return;
      }
      
      // FALLBACK: Fetch from API using preset_id (not URL slug)
      const token = localStorage.getItem("session_token");
      const templateCategory = effectiveCategory || category;
      const res = await fetch(`${API}/admin/pilot-templates/${templateCategory}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplate(data.template);
        setQuestions(data.template?.intake_questions || []);
      }
    } catch (err) {
      console.error("Failed to fetch template:", err);
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const templateCategory = effectiveCategory || category;
      
      // Save to the global template
      const res = await fetch(`${API}/admin/pilot-templates/${templateCategory}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...template,
          intake_questions: questions,
        })
      });
      
      // Also save to the sub-category if we have a subCategoryId
      if (subCategoryId) {
        await fetch(`${API}/admin/pilot-templates-by-subcategory/${subCategoryId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            sections: template?.sections || [],
            intake_questions: questions,
          })
        });
      }
      
      if (res.ok) {
        toast.success("Template saved!");
        setHasChanges(false);
        onRefresh?.();
      } else {
        toast.error("Failed to save template");
      }
    } catch (err) {
      toast.error("Error saving template");
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = (newQuestion) => {
    setQuestions([...questions, newQuestion]);
    setHasChanges(true);
  };

  const handleUpdateQuestion = (idx, updated) => {
    const newQuestions = [...questions];
    newQuestions[idx] = updated;
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const handleRemoveQuestion = (idx) => {
    if (!window.confirm("Remove this question?")) return;
    setQuestions(questions.filter((_, i) => i !== idx));
    setHasChanges(true);
  };

  const handleMoveQuestion = (idx, direction) => {
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= questions.length) return;
    const newQuestions = [...questions];
    [newQuestions[idx], newQuestions[newIdx]] = [newQuestions[newIdx], newQuestions[idx]];
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="template-editor-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardList className="w-5 h-5" /> Intake Form Builder
          </h3>
          <p className="text-sm text-gray-500">
            Customize the intake questions for {effectiveCategory || category} pilots
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
              Unsaved changes
            </Badge>
          )}
          <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="preview-form-btn">
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges} className={categoryColors.bg} data-testid="save-template-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Template
          </Button>
        </div>
      </div>

      {/* Template Settings Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4" /> Template Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={template?.defaults?.require_nda !== false}
                onCheckedChange={(v) => {
                  setTemplate({ ...template, defaults: { ...template?.defaults, require_nda: v } });
                  setHasChanges(true);
                }}
              />
              <div>
                <p className="text-sm font-medium">Require NDA</p>
                <p className="text-xs text-gray-500">Before intake</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={template?.defaults?.require_intake !== false}
                onCheckedChange={(v) => {
                  setTemplate({ ...template, defaults: { ...template?.defaults, require_intake: v } });
                  setHasChanges(true);
                }}
              />
              <div>
                <p className="text-sm font-medium">Require Intake</p>
                <p className="text-xs text-gray-500">Form completion</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Trial Period (Days)</Label>
              <Input
                type="number"
                value={template?.defaults?.trial_period_days || 60}
                onChange={(e) => {
                  setTemplate({ ...template, defaults: { ...template?.defaults, trial_period_days: parseInt(e.target.value) } });
                  setHasChanges(true);
                }}
                className="h-8"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={template?.defaults?.nfc_reader_included !== false}
                onCheckedChange={(v) => {
                  setTemplate({ ...template, defaults: { ...template?.defaults, nfc_reader_included: v } });
                  setHasChanges(true);
                }}
              />
              <div>
                <p className="text-sm font-medium">NFC Reader</p>
                <p className="text-xs text-gray-500">Include in pilot</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Intake Questions ({questions.length})</CardTitle>
            <Button size="sm" onClick={() => setShowAddQuestion(true)} data-testid="add-question-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Question
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {questions.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No questions yet</p>
              <p className="text-sm text-gray-400 mt-1">Add questions to build your intake form</p>
              <Button className="mt-4" onClick={() => setShowAddQuestion(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add First Question
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((question, idx) => (
                <QuestionEditor
                  key={question.id}
                  question={question}
                  onUpdate={(updated) => handleUpdateQuestion(idx, updated)}
                  onRemove={() => handleRemoveQuestion(idx)}
                  onMoveUp={() => handleMoveQuestion(idx, "up")}
                  onMoveDown={() => handleMoveQuestion(idx, "down")}
                  isFirst={idx === 0}
                  isLast={idx === questions.length - 1}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Questions", value: questions.length, icon: List },
          { label: "Required", value: questions.filter(q => q.required).length, icon: Sparkles },
          { label: "Multi-Select", value: questions.filter(q => q.type === "multiselect").length, icon: CheckSquare },
          { label: "Text Fields", value: questions.filter(q => ["text", "textarea", "email", "phone"].includes(q.type)).length, icon: Type },
        ].map((stat, i) => (
          <Card key={i}>
            <CardContent className="pt-4 pb-4 flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
              <stat.icon className="w-6 h-6 text-gray-300" />
            </CardContent>
          </Card>
        ))}
      </div>

      <AddQuestionDialog
        isOpen={showAddQuestion}
        onClose={() => setShowAddQuestion(false)}
        onAdd={handleAddQuestion}
      />

      <PreviewDialog
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        questions={questions}
        categoryColors={categoryColors}
      />
    </div>
  );
}
